<template>
  <new-modal-dialog
    v-model="internalDialog"
    title="Extra info fields"
    max-width="1100"
    persistent
    scrollable
  >
    <p class="text-body-2 text--secondary mb-4">
      Add and order fields, then set labels and options. Changes apply after you save.
    </p>

    <!-- Add field -->
    <v-sheet
      outlined
      rounded
      class="pa-3 mb-1"
    >
      <v-row
        align="center"
        dense
      >
        <v-col
          cols="12"
          sm="7"
        >
          <v-select
            v-model="newType"
            :items="fieldTypeOptions"
            item-text="text"
            item-value="value"
            label="Field type"
            placeholder="Choose a type to add"
            clearable
            dense
            hide-details
            outlined
          />
        </v-col>
        <v-col
          cols="12"
          sm="5"
        >
          <v-btn
            color="primary"
            block
            :disabled="!newType"
            @click="addField"
          >
            <v-icon
              small
              left
            >
              mdi-plus
            </v-icon>
            Add to list
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <v-divider class="my-4" />

    <div
      v-if="!fields.length"
      class="text-center py-8 px-2 rounded grey lighten-4"
    >
      <p class="text-subtitle-2 mb-1">
        No fields yet
      </p>
      <p class="text-body-2 text--secondary ma-0">
        Pick a type above, then add it to the list. You can reorder fields anytime.
      </p>
    </div>

    <!-- Fields -->
    <v-expansion-panels
      v-else
      multiple
      flat
    >
      <v-expansion-panel
        v-for="(field, index) in fields"
        :key="index"
        class="mb-1 rounded elevation-1"
      >
        <v-expansion-panel-header class="px-2">
          <v-row
            align="center"
            class="w-100 ma-0"
            dense
            no-gutters
          >
            <v-col
              cols="12"
              sm="3"
              :title="fieldTypeLabel(field.type)"
              class="py-1 pr-1"
            >
              <span
                class="d-block text-truncate text-caption text-uppercase text--secondary font-weight-bold"
              >{{
                fieldTypeLabel(field.type)
              }}</span>
            </v-col>
            <v-col
              cols="12"
              sm="5"
              :title="firstLabel(field) || 'No label'"
              class="py-1 pr-1"
              style="min-width:0"
            >
              <span class="text-body-2 text-truncate d-block">
                {{ firstLabel(field) || 'No label' }}
              </span>
            </v-col>
            <v-col
              cols="12"
              sm="1"
              class="d-none d-sm-flex align-center justify-center flex-shrink-0 py-1"
            >
              <span
                v-if="!hasValue(field.type)"
                class="d-block overline text--disabled"
              >Display</span>
            </v-col>
            <v-col
              cols="12"
              sm="3"
              class="d-flex flex-nowrap align-center justify-end flex-shrink-0 py-1 pl-0"
              @click.stop
            >
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    :disabled="index === 0"
                    class="ml-0"
                    icon
                    x-small
                    v-on="on"
                    @click="moveUp(index)"
                  >
                    <v-icon small>
                      mdi-arrow-up
                    </v-icon>
                  </v-btn>
                </template>
                Move up
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    :disabled="index === fields.length - 1"
                    class="ml-0"
                    icon
                    x-small
                    v-on="on"
                    @click="moveDown(index)"
                  >
                    <v-icon small>
                      mdi-arrow-down
                    </v-icon>
                  </v-btn>
                </template>
                Move down
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    class="ml-0"
                    color="error"
                    icon
                    x-small
                    v-on="on"
                    @click="removeField(index)"
                  >
                    <v-icon small>
                      mdi-delete-outline
                    </v-icon>
                  </v-btn>
                </template>
                Remove field
              </v-tooltip>
            </v-col>
          </v-row>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="pt-0">
          <!-- Labels -->
          <v-card
            class="mb-3 pa-3"
            elevation="0"
            outlined
          >
            <div class="d-flex flex-column flex-sm-row align-sm-baseline justify-space-between mb-2">
              <span class="subtitle-2">Labels</span>
              <span
                v-if="field.type === 'divider'"
                class="caption text--secondary"
              >Not shown; optional for your reference</span>
              <span
                v-else
                class="caption text--secondary"
              >Visible to guests (per language)</span>
            </div>
            <v-row>
              <v-col
                v-for="lang in languages"
                :key="lang"
                cols="12"
                md="6"
                class="py-1"
              >
                <v-text-field
                  v-model="field.label[lang]"
                  :label="String(lang).toUpperCase()"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-card>

          <v-card
            v-if="hasValue(field.type) && field.type !== 'selection'"
            class="mb-3 pa-3"
            elevation="0"
            outlined
          >
            <div class="d-flex flex-column flex-sm-row align-sm-baseline justify-space-between mb-2">
              <span class="subtitle-2">Validation & default</span>
              <span class="caption text--secondary">What guests see and what is mandatory</span>
            </div>
            <v-row
              align="center"
              dense
            >
              <v-col
                cols="12"
                :md="field.type === 'number' ? 6 : 12"
                class="py-1"
              >
                <v-checkbox
                  v-model="field.required"
                  label="Required"
                  dense
                  hide-details
                />
              </v-col>
              <v-col
                v-if="field.type === 'number'"
                cols="12"
                md="6"
                class="py-1"
              >
                <v-checkbox
                  v-model="field.maxPartySize"
                  label="Max value is party size"
                  dense
                  hide-details
                  @change="onMaxPartySizeChanged(field)"
                />
              </v-col>
            </v-row>

            <v-text-field
              v-if="field.type === 'string'"
              v-model="field.value"
              class="mt-1"
              label="Default value"
              hint="Optional pre-fill; only if the field is not required to start empty"
              dense
              outlined
              hide-details="auto"
              persistent-hint
            />

            <v-textarea
              v-else-if="field.type === 'text'"
              v-model="field.value"
              class="mt-1"
              label="Default value"
              hint="Optional pre-fill for multi-line text"
              dense
              outlined
              hide-details="auto"
              persistent-hint
            />

            <v-text-field
              v-else-if="field.type === 'number'"
              v-model.number="field.value"
              class="mt-1"
              type="number"
              label="Default value"
              hint="Pre-filled when the widget opens"
              dense
              outlined
              hide-details="auto"
              persistent-hint
            />

            <v-checkbox
              v-else-if="field.type === 'boolean'"
              v-model="field.value"
              class="mt-1"
              label="On by default (checked)"
              dense
              hide-details
            />
          </v-card>

          <!-- String / text length -->
          <v-card
            v-if="field.type === 'string' || field.type === 'text'"
            class="mb-3 pa-3"
            elevation="0"
            outlined
          >
            <div class="d-flex flex-column flex-sm-row align-sm-baseline justify-space-between mb-2">
              <span class="subtitle-2">Length</span>
              <span class="caption text--secondary">Optional character limits</span>
            </div>
            <v-row dense>
              <v-col
                cols="6"
                class="py-1"
              >
                <v-text-field
                  v-model.number="field.minLength"
                  label="Min"
                  type="number"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
              <v-col
                cols="6"
                class="py-1"
              >
                <v-text-field
                  v-model.number="field.maxLength"
                  label="Max"
                  type="number"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-card>

          <!-- Number min/max -->
          <v-card
            v-if="field.type === 'number'"
            class="mb-3 pa-3"
            elevation="0"
            outlined
          >
            <div class="d-flex flex-column flex-sm-row align-sm-baseline justify-space-between mb-2">
              <span class="subtitle-2">Range</span>
              <span class="caption text--secondary">Min / max on the number input</span>
            </div>
            <v-row dense>
              <v-col
                cols="6"
                class="py-1"
              >
                <v-text-field
                  v-model.number="field.min"
                  label="Min"
                  type="number"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
              <v-col
                cols="6"
                class="py-1"
              >
                <v-text-field
                  v-model.number="field.max"
                  label="Max"
                  type="number"
                  :disabled="!!field.maxPartySize"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-card>

          <!-- Selection: options -->
          <v-card
            v-if="field.type === 'selection'"
            class="mb-3 pa-3"
            elevation="0"
            outlined
          >
            <div class="d-flex flex-column flex-sm-row align-sm-baseline justify-space-between mb-2">
              <span class="subtitle-2">Options</span>
              <span class="caption text--secondary">{{ (field.options && field.options.length) || 0 }} defined</span>
            </div>

            <v-card
              v-for="(opt, optIndex) in field.options"
              :key="optIndex"
              outlined
              class="pa-3 mb-3"
            >
              <v-row dense>
                <v-col
                  v-for="lang in languages"
                  :key="lang"
                  cols="12"
                  md="6"
                  class="py-1"
                >
                  <v-text-field
                    v-model="opt.label[lang]"
                    :label="String(lang).toUpperCase()"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
              </v-row>

              <v-btn
                class="px-0"
                text
                small
                color="error"
                @click="removeOption(field, optIndex)"
              >
                Remove this option
              </v-btn>
            </v-card>

            <v-btn
              small
              outlined
              color="primary"
              class="mb-4"
              @click="addOption(field)"
            >
              <v-icon
                small
                left
              >
                mdi-plus
              </v-icon>
              Add option
            </v-btn>

            <v-divider class="mb-3" />

            <v-checkbox
              v-model="field.required"
              class="mb-2"
              label="Required (guest must choose an option)"
              dense
              hide-details
            />
            <p class="caption text--secondary mb-1">
              Preselected: which option is selected when the widget opens.
            </p>
            <v-select
              v-model.number="field.value"
              :items="selectionItems(field)"
              label="Preselected option"
              :disabled="!field.options || !field.options.length"
              dense
              outlined
              hide-details
            />
          </v-card>

          <!-- Display: heading, literal, divider -->
          <v-card
            v-if="isDisplayField(field.type)"
            class="pa-3"
            elevation="0"
            outlined
          >
            <div class="d-flex flex-column flex-sm-row align-sm-baseline justify-space-between mb-2">
              <span class="subtitle-2">Appearance</span>
              <span class="caption text--secondary">
                <template v-if="field.type === 'divider'">How the line is styled in the form</template>
                <template v-else>Alignment and emphasis</template>
              </span>
            </div>
            <v-row dense>
              <v-col
                cols="12"
                sm="4"
                class="py-1"
              >
                <v-select
                  v-model="field.align"
                  :items="['center','left','right']"
                  label="Align"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
                class="py-1"
              >
                <v-select
                  v-model="field.variant"
                  :items="['primary','secondary']"
                  label="Variant"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
                class="py-1"
              >
                <v-select
                  v-model="field.size"
                  :items="['md','sm','xs']"
                  label="Size"
                  dense
                  outlined
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-divider class="my-6" />

    <v-card
      elevation="0"
      outlined
    >
      <v-expansion-panels
        v-model="jsonPanel"
        flat
      >
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-subtitle-2">JSON preview</span>
            <span class="caption text--secondary ml-2 d-none d-sm-inline">(advanced)</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p class="caption text--secondary mb-2">
              Same structure as stored when you save. Use to verify the configuration.
            </p>
            <v-textarea
              class="json-textarea"
              :value="formattedJson"
              readonly
              outlined
              rows="8"
              hide-details
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <v-dialog
      v-model="showCloseConfirm"
      max-width="520"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          Discard unsaved changes?
        </v-card-title>
        <v-card-text>
          You have unsaved changes. If you close now, they will be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showCloseConfirm = false"
          >
            Keep editing
          </v-btn>
          <v-btn
            color="red"
            @click="discardAndClose"
          >
            Discard changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template #actions>
      <v-spacer />
      <v-btn
        color="primary"
        @click="save"
      >
        Save
      </v-btn>
      <v-btn
        text
        @click="requestClose"
      >
        Cancel
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup>
import {
  ref,
  watch,
  computed,
  getCurrentInstance,
} from 'vue';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';

const props = defineProps({
  value: { type: String, default: () => '[]' },
  languages: { type: Array, required: true },
  dialog: { type: Boolean, default: true },
});

const emit = defineEmits(['input', 'save', 'close']);

const internalDialog = ref(props.dialog);

const fieldTypes = [
  'heading',
  'literal',
  'divider',
  'string',
  'text',
  'number',
  'boolean',
  'selection',
];

const FIELD_TYPE_LABELS = {
  heading: 'Heading',
  literal: 'Static text',
  divider: 'Divider',
  string: 'Short text',
  text: 'Long text',
  number: 'Number',
  boolean: 'Yes / no',
  selection: 'Single choice',
};

function fieldTypeLabel(type) {
  return FIELD_TYPE_LABELS[type] || type;
}

const fieldTypeOptions = fieldTypes.map((value) => ({
  value,
  text: fieldTypeLabel(value),
}));

const jsonPanel = ref(null);

const DISPLAY_FIELD_TYPES = ['heading', 'literal', 'divider'];

function applyDisplayFieldDefaults(item) {
  if (!item || !DISPLAY_FIELD_TYPES.includes(item.type)) {
    return item;
  }
  return {
    ...item,
    align: item.align != null && item.align !== '' ? item.align : 'center',
    variant: item.variant != null && item.variant !== '' ? item.variant : 'primary',
    size: item.size != null && item.size !== '' ? item.size : 'md',
  };
}

// const clone = (obj) => JSON.parse(JSON.stringify(obj));

// const fields = ref(clone(props.modelValue));
const fields = ref(
  JSON.parse(props.value).map(applyDisplayFieldDefaults),
);
const newType = ref(null);
const showCloseConfirm = ref(false);
const baselineSerialized = ref('');

// watch(fields, (val) => {
//   emit('input', JSON.stringify(val));
// }, { deep: true });

function serializeFields(list) {
  return (list || []).map((f) => {
    const out = { ...f };

    if (out.type === 'number') {
      const maxPartySize = !!out.maxPartySize;
      if (maxPartySize) {
        delete out.max;
        out.maxPartySize = true;
      } else {
        delete out.maxPartySize;
      }
    }

    return out;
  });
}

watch(() => props.value, (val) => {
  const parsed = JSON.parse(val).map(applyDisplayFieldDefaults);
  fields.value = parsed;
  baselineSerialized.value = JSON.stringify(serializeFields(parsed));
});

const formattedJson = computed(() => JSON.stringify(serializeFields(fields.value), null, 2));
const isDirty = computed(() => JSON.stringify(serializeFields(fields.value)) !== baselineSerialized.value);

function baseField(type) {
  const label = {};
  props.languages.forEach((l) => { label[l] = ''; });

  const field = { type, label };

  if (!DISPLAY_FIELD_TYPES.includes(type)) {
    field.required = false;
  }

  if (DISPLAY_FIELD_TYPES.includes(type)) {
    field.align = 'center';
    field.variant = 'primary';
    field.size = 'md';
  }

  if (type === 'number') {
    field.maxPartySize = false;
  }

  if (type === 'selection') {
    field.options = [];
    field.value = 0;
  }

  return field;
}

function addField() {
  fields.value.push(baseField(newType.value));
  newType.value = null;
}

function removeField(index) {
  fields.value.splice(index, 1);
}

function addOption(field) {
  const label = {};
  props.languages.forEach((l) => { label[l] = ''; });
  field.options.push({ label });
}

function removeOption(field, index) {
  field.options.splice(index, 1);
}

function hasValue(type) {
  return !DISPLAY_FIELD_TYPES.includes(type);
}

function isDisplayField(type) {
  return DISPLAY_FIELD_TYPES.includes(type);
}

function selectionItems(field) {
  return field.options.map((o, i) => ({
    text: o.label[props.languages[0]] || `Option ${i}`,
    value: i,
  }));
}

function firstLabel(field) {
  const label = field.label || {};

  const proxy = getCurrentInstance()?.proxy;
  const uiLocale = proxy?.$i18n?.locale || null;
  const uiLang = uiLocale ? String(uiLocale).split('-')[0] : null;

  const uiExact = uiLocale && label[uiLocale];
  if (uiExact) return uiExact;

  const uiBase = uiLang && label[uiLang];
  if (uiBase) return uiBase;

  const firstDefined = (props.languages || [])
    .map((l) => label[l])
    .find((v) => typeof v === 'string' && v.trim().length > 0);
  if (firstDefined) return firstDefined;

  return label?.[props.languages[0]];
}

function closeNow() {
  internalDialog.value = false;
  emit('close');
}

function requestClose() {
  if (!isDirty.value) {
    closeNow();
    return;
  }

  showCloseConfirm.value = true;
}

function discardAndClose() {
  showCloseConfirm.value = false;
  closeNow();
}

function onMaxPartySizeChanged(f) {
  const field = f;
  if (!field || field.type !== 'number') return;
  if (field.maxPartySize) {
    delete field.max;
    if (field.min === undefined || field.min === null || field.min === '') {
      field.min = 0;
    }
  }
}

function save() {
  const serialized = JSON.stringify(serializeFields(fields.value));
  emit('save', serialized);
  baselineSerialized.value = serialized;
  closeNow();
}

function moveUp(index) {
  if (index === 0) return;

  const temp = fields.value[index - 1];
  fields.value.splice(index - 1, 1, fields.value[index]);
  fields.value.splice(index, 1, temp);
}

function moveDown(index) {
  if (index === fields.value.length - 1) return;

  const temp = fields.value[index + 1];
  fields.value.splice(index + 1, 1, fields.value[index]);
  fields.value.splice(index, 1, temp);
}
</script>
<style scoped>
/* Vuetify has no monospace preset for v-textarea */
.json-textarea ::v-deep(textarea) {
  font: 12px/1.4 Roboto Mono, Consolas, monospace;
}

</style>
